// =============================================
// wpConnectionTest — realny test "czy domena nadaje się do publikacji"
//
// GET /users/me to za mało: credentials mogą działać do odczytu, a publish
// i tak padnie (brak cap edit_posts, plugin security blokujący POST, itd.).
// Test robi to co robi pipeline naprawdę: tworzy SZKIC przez WP REST,
// a po sukcesie od razu go usuwa (force=true — omija kosz, zero śladu).
//
// CELOWO bez singletona wordpressService: bulk test biegnie w worker pool
// (3 równolegle), a setCredentials na singletonie to race — szkic mógłby
// polecieć na cudzą domenę. Każdy call ma własne credentials w closure.
//
// Wynik zapisywany w websites.connection_status / connection_checked_at /
// connection_error — kolumna STATUS w tabeli Domeny czyta stamtąd.
// =============================================
import { supabase } from "@/integrations/supabase/client";
import type { Website } from "@/hooks/useWebsites";

export interface ConnectionTestResult {
  ok: boolean;
  error?: string;
}

const isEmptyVal = (v?: string | null) => !v || v.trim() === "" || v.trim().toUpperCase() === "EMPTY";

const TEST_TIMEOUT_MS = 20_000;

async function wpFetch(
  base: string,
  authHeader: string,
  endpoint: string,
  init: RequestInit = {},
): Promise<{ status: number; json: any }> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TEST_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/wp-json/wp/v2${endpoint}`, {
      ...init,
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/json",
        ...(init.headers || {}),
      },
      signal: controller.signal,
    });
    let json: any = null;
    try {
      json = await res.json();
    } catch {
      // non-JSON response (np. HTML strona błędu / WAF) — json zostaje null
    }
    return { status: res.status, json };
  } finally {
    clearTimeout(timer);
  }
}

/** Skrócony opis błędu WP REST (code + message) albo HTTP status. */
function describeError(status: number, json: any): string {
  if (json?.code || json?.message) {
    return `${json.code || `HTTP ${status}`}: ${String(json.message || "").slice(0, 140)}`;
  }
  return `HTTP ${status}`;
}

/**
 * Test pojedynczej domeny: szkic → delete → zapis statusu do DB.
 * Nigdy nie rzuca — błąd ląduje w {ok:false, error} + w DB.
 */
export async function testWebsiteConnection(website: Website): Promise<ConnectionTestResult> {
  const username = !isEmptyVal(website.login) ? website.login!.trim() : null;
  const password = !isEmptyVal(website.api_key)
    ? website.api_key!.trim()
    : !isEmptyVal(website.password)
      ? website.password!.trim()
      : null;
  const url = !isEmptyVal(website.url) ? website.url.trim().replace(/\/+$/, "") : null;

  let result: ConnectionTestResult;

  if (!url) {
    result = { ok: false, error: "Brak URL domeny" };
  } else if (!username || !password) {
    result = { ok: false, error: "Brak loginu lub hasła/API key" };
  } else {
    const authHeader = `Basic ${btoa(`${username}:${password}`)}`;
    try {
      const create = await wpFetch(url, authHeader, "/posts", {
        method: "POST",
        body: JSON.stringify({
          title: "PBN-SD — test połączenia (auto-usuwany)",
          content: "<p>Automatyczny test połączenia z panelu Domeny. Ten szkic usuwa się sam.</p>",
          status: "draft",
        }),
      });

      if (create.status === 201 && create.json?.id) {
        // Szkic powstał = publish capability potwierdzona. Sprzątanie best-effort:
        // nieudany delete NIE unieważnia testu (zostaje szkic do ręcznego usunięcia).
        const del = await wpFetch(url, authHeader, `/posts/${create.json.id}?force=true`, {
          method: "DELETE",
        });
        result =
          del.status >= 200 && del.status < 300
            ? { ok: true }
            : { ok: true, error: `Szkic #${create.json.id} utworzony, ale nie usunięty — usuń ręcznie w WP` };
      } else {
        result = { ok: false, error: describeError(create.status, create.json) };
      }
    } catch (e) {
      const msg =
        e instanceof DOMException && e.name === "AbortError"
          ? `Timeout ${TEST_TIMEOUT_MS / 1000}s — brak odpowiedzi`
          : e instanceof Error
            ? e.message
            : "Test połączenia padł";
      // "Failed to fetch" w przeglądarce = najczęściej CORS / DNS / brak HTTPS
      result = {
        ok: false,
        error: msg === "Failed to fetch" ? "Failed to fetch (CORS / DNS / domena nie odpowiada)" : msg,
      };
    }
  }

  await (supabase as any)
    .from("websites")
    .update({
      connection_status: result.ok ? "ok" : "error",
      connection_checked_at: new Date().toISOString(),
      connection_error: result.error || null,
    })
    .eq("id", website.id);

  return result;
}

/**
 * Bulk test — worker pool (default 3 równolegle), onProgress po każdej domenie.
 * Zwraca mapę websiteId → wynik. Parallel-safe (zero shared state).
 */
export async function testWebsitesBulk(
  websites: Website[],
  onProgress?: (done: number, total: number, current: Website, result: ConnectionTestResult) => void,
  concurrency = 3,
): Promise<Map<string, ConnectionTestResult>> {
  const results = new Map<string, ConnectionTestResult>();
  const queue = [...websites];
  let done = 0;

  const worker = async () => {
    while (queue.length > 0) {
      const site = queue.shift();
      if (!site) break;
      const res = await testWebsiteConnection(site);
      results.set(site.id, res);
      done++;
      onProgress?.(done, websites.length, site, res);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, websites.length) }, () => worker()),
  );
  return results;
}
